import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/feature/LanguageSwitcher';
import Footer from '@/components/feature/Footer';
import { MAIN_SITE_URL } from '@/mocks/categories';
import JsonLd from '@/seo/JsonLd';
import { SITE_URL, SITE_NAME, FAVICON_URL } from '@/seo/schema';

const TABLE_ROWS = [
  { col1: 'article.row1_col1', col2: 'article.row1_col2' },
  { col1: 'article.row2_col1', col2: 'article.row2_col2' },
  { col1: 'article.row3_col1', col2: 'article.row3_col2' },
  { col1: 'article.row4_col1', col2: 'article.row4_col2' },
  { col1: 'article.row5_col1', col2: 'article.row5_col2' },
  { col1: 'article.row6_col1', col2: 'article.row6_col2' },
];

const FAQ_ITEMS = [
  { q: 'article.faq_q1', a: 'article.faq_a1' },
  { q: 'article.faq_q2', a: 'article.faq_a2' },
  { q: 'article.faq_q3', a: 'article.faq_a3' },
];

const TOC_ITEMS = [
  { label: 'article.toc_1', href: '#toc-1' },
  { label: 'article.toc_2', href: '#toc-2' },
  { label: 'article.toc_3', href: '#toc-3' },
  { label: 'article.toc_4', href: '#toc-4' },
  { label: 'article.toc_5', href: '#toc-5' },
  { label: 'article.toc_6', href: '#toc-6' },
];

export default function ArticlePage() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    const html = document.documentElement;
    const prevScrollBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    html.style.scrollBehavior = prevScrollBehavior;
  }, []);

  const pageUrl = `${SITE_URL}/usfans-spreadsheet-guide`;

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: t('article.title'),
    description: t('article.meta_description'),
    author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: { '@type': 'ImageObject', url: FAVICON_URL },
    },
    mainEntityOfPage: pageUrl,
    datePublished: '2026-08-23',
    dateModified: '2026-08-23',
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('article.breadcrumb_home'), item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: t('article.breadcrumb_current'), item: pageUrl },
    ],
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map((item) => ({
      '@type': 'Question',
      name: t(item.q),
      acceptedAnswer: { '@type': 'Answer', text: t(item.a) },
    })),
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleLd} />
      <JsonLd data={breadcrumbLd} />
      <JsonLd data={faqLd} />
      <header className="sticky top-0 z-40 border-b border-background-200 bg-white">
        <div className="flex items-center justify-between gap-4 px-4 py-3 md:px-6">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-file-list-3-line text-lg" />
            </span>
            <span className="font-heading text-base font-bold text-foreground-900 whitespace-nowrap">
              USFans <span className="text-primary-600">Spreadsheet</span>
            </span>
          </Link>

          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="hidden items-center gap-1.5 rounded-full border border-background-300/70 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 sm:flex whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              {t('article.back_home')}
            </Link>
            <LanguageSwitcher />
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="hidden items-center gap-1.5 rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 md:flex whitespace-nowrap cursor-pointer"
            >
              {t('nav.visit')}
              <i className="ri-arrow-right-up-line" />
            </a>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-14">
        <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="Breadcrumb">
          <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
            {t('article.breadcrumb_home')}
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-700">{t('article.breadcrumb_current')}</span>
        </nav>

        <article className="mt-6">
          <h1 className="font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
            {t('article.title')}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-time-line" />
              {t('article.read_time')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-refresh-line" />
              {t('article.updated')}: 2026
            </span>
          </div>

          <div className="mt-6 rounded-xl border border-background-200/70 bg-background-100 p-5">
            <p className="text-sm font-semibold text-foreground-900">{t('article.toc')}</p>
            <ul className="mt-3 space-y-2">
              {TOC_ITEMS.map((item) => (
                <li key={item.href}>
                  <a href={item.href} className="text-sm text-foreground-600 transition-colors hover:text-primary-600">
                    {t(item.label)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <p className="mt-8 text-base leading-relaxed text-foreground-700">{t('article.intro')}</p>

          <h2 id="toc-1" className="mt-10 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            {t('article.h2_1')}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">{t('article.p_1a')}</p>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">{t('article.p_1b')}</p>

          <h2 id="toc-2" className="mt-10 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            {t('article.h2_2')}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">{t('article.p_2a')}</p>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">{t('article.p_2b')}</p>

          <h2 id="toc-3" className="mt-10 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            {t('article.h2_3')}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">{t('article.p_3a')}</p>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">{t('article.p_3b')}</p>

          <h2 id="toc-4" className="mt-10 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            {t('article.h2_4')}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">{t('article.table_header')}</p>
          <div className="mt-4 overflow-x-auto rounded-xl border border-background-200/70">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="bg-background-100 text-xs text-foreground-500">
                  <th className="px-4 py-3 font-semibold">{t('article.table_col1')}</th>
                  <th className="px-4 py-3 font-semibold">{t('article.table_col2')}</th>
                </tr>
              </thead>
              <tbody>
                {TABLE_ROWS.map((row, i) => (
                  <tr key={i} className="border-t border-background-200/70">
                    <td className="px-4 py-3 font-medium text-foreground-800">{t(row.col1)}</td>
                    <td className="px-4 py-3 text-foreground-600">{t(row.col2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 id="toc-5" className="mt-10 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            {t('article.h2_5')}
          </h2>
          <ol className="mt-4 list-decimal space-y-2.5 pl-5 text-base leading-relaxed text-foreground-700">
            {[1, 2, 3, 4].map((n) => (
              <li key={n}>{t(`article.tip_${n}`)}</li>
            ))}
          </ol>

          <h2 id="toc-6" className="mt-10 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            {t('article.faq_title')}
          </h2>
          <div className="mt-4 space-y-3">
            {FAQ_ITEMS.map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={item.q} className="overflow-hidden rounded-xl border border-background-200/70 bg-background-50">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                  >
                    <span className="text-sm font-semibold text-foreground-900">{t(item.q)}</span>
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-500 transition-transform ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    >
                      <i className="ri-arrow-down-s-line" />
                    </span>
                  </button>
                  {isOpen && <p className="px-5 pb-5 text-sm leading-relaxed text-foreground-600">{t(item.a)}</p>}
                </div>
              );
            })}
          </div>

          <div className="mt-12 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 p-8 text-background-50">
            <h2 className="font-heading text-xl font-bold md:text-2xl">{t('article.cta_title')}</h2>
            <p className="mt-3 text-sm leading-relaxed text-background-50/90">{t('article.cta_body')}</p>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100 whitespace-nowrap cursor-pointer"
            >
              {t('article.cta_button')}
              <i className="ri-arrow-right-up-line" />
            </a>
          </div>
        </article>

        <div className="mt-10">
          <h3 className="font-heading text-base font-bold text-foreground-950">{t('article.related_title')}</h3>
          <div className="mt-4 flex flex-wrap gap-3">
            {[1, 2, 3].map((n) => (
              <Link
                key={n}
                to="/"
                className="inline-flex items-center gap-1.5 rounded-full border border-background-300/70 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer whitespace-nowrap"
              >
                {t(`article.related_${n}`)}
                <i className="ri-arrow-right-line" />
              </Link>
            ))}
          </div>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            {t('article.back_home')}
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}